import { action, computed, makeObservable, observable } from 'mobx';

/**
 * Ephemeral UI chrome state that other surfaces need to coordinate with —
 * e.g. the global SearchBar's measured position, so the bottom-center
 * toaster can lift its offset above it (only when the bar would actually
 * overlap toasts) instead of being a static offset that's wrong half the
 * time.
 *
 * Not persisted; not synced. Components publish measurements in a
 * useLayoutEffect on render + ResizeObserver + window resize +
 * transitionend.
 */
export class UIChromeStore {
  /** Distance from viewport bottom to the SearchBar's top edge, in px.
   * 0 when no SearchBar is mounted. */
  @observable
  searchBarTopFromBottom: number = 0;

  /** Distance from viewport left to the SearchBar's left edge, in px.
   * -1 sentinel = no SearchBar mounted (0 is a valid measurement). */
  @observable
  searchBarLeftFromLeft: number = -1;

  constructor() {
    makeObservable(this);
  }

  @computed
  get isSearchBarMeasured(): boolean {
    return this.searchBarLeftFromLeft >= 0;
  }

  /** Toast offset in px above the viewport bottom. 96 (HTML spec's
   * `.toast-stack { bottom: 96px }`) is the floor — used when there's no
   * SearchBar mounted. Otherwise the toast sits 16px above the bar's top
   * edge so toasts and bar never visually overlap, regardless of where the
   * bar happens to be in its current state (empty / active / mobile). */
  @computed
  get toastOffsetPx(): number {
    const DEFAULT = 96;
    const GAP = 16;
    if (!this.isSearchBarMeasured) return DEFAULT;
    return Math.max(DEFAULT, this.searchBarTopFromBottom + GAP);
  }

  /** Toast left-edge in px from viewport left. Null = use sonner's default
   * bottom-center horizontal positioning (no SearchBar mounted). When the
   * bar is mounted we left-align the toast stack to the bar's left edge so
   * the toast follows the content area instead of the raw viewport — which
   * matters when there's a left navigation rail offsetting the content. */
  @computed
  get toastLeftPx(): number | null {
    if (!this.isSearchBarMeasured) return null;
    return this.searchBarLeftFromLeft;
  }

  @action
  setSearchBarRect(topFromBottom: number, leftFromLeft: number) {
    // No-op when nothing actually moved — avoids gratuitous MobX reactions
    // (and downstream WithToaster re-renders) on hover/focus that don't
    // change geometry.
    if (
      this.searchBarTopFromBottom === topFromBottom &&
      this.searchBarLeftFromLeft === leftFromLeft
    ) {
      return;
    }
    this.searchBarTopFromBottom = topFromBottom;
    this.searchBarLeftFromLeft = leftFromLeft;
  }

  @action
  clearSearchBarRect() {
    this.searchBarTopFromBottom = 0;
    this.searchBarLeftFromLeft = -1;
  }
}

export const uiChromeStore = new UIChromeStore();
